import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * @test-file: AppHeader.test.tsx
 * @test-coverage: 20 tests (navigation, dropdowns, breadcrumbs, variants, mobile menu)
 * @last-updated: 2025-12-28
 */
import React, { useState, useCallback, useEffect, useRef } from 'react';
import './AppHeader.css';
/**
 * NavDropdown - Internal dropdown menu component
 */
function NavDropdown({ label, items, columns = 1, icon, LinkComponent, onNavClick, activeItem, }) {
    const [isOpen, setIsOpen] = useState(false);
    const timeoutRef = useRef(null);
    const handleMouseEnter = useCallback(() => {
        if (timeoutRef.current)
            clearTimeout(timeoutRef.current);
        setIsOpen(true);
    }, []);
    const handleMouseLeave = useCallback(() => {
        timeoutRef.current = setTimeout(() => setIsOpen(false), 180);
    }, []);
    useEffect(() => {
        return () => {
            if (timeoutRef.current)
                clearTimeout(timeoutRef.current);
        };
    }, []);
    const Link = LinkComponent || 'a';
    return (_jsxs("div", { className: "app-header__dropdown", onMouseEnter: handleMouseEnter, onMouseLeave: handleMouseLeave, children: [_jsxs("button", { className: "app-header__nav-btn app-header__dropdown-trigger", "aria-expanded": isOpen, "aria-haspopup": "true", children: [icon && _jsx("span", { className: "app-header__nav-icon", children: icon }), label, _jsx("svg", { className: "app-header__chevron", viewBox: "0 0 20 20", fill: "currentColor", width: "16", height: "16", children: _jsx("path", { fillRule: "evenodd", d: "M5.23 7.21a.75.75 0 011.06.02L10 11.168l3.71-3.938a.75.75 0 111.08 1.04l-4.25 4.5a.75.75 0 01-1.08 0l-4.25-4.5a.75.75 0 01.02-1.06z", clipRule: "evenodd" }) })] }), isOpen && (_jsx("div", { className: `app-header__dropdown-menu ${columns === 2 ? 'app-header__dropdown-menu--two-col' : ''}`, children: items.map((item) => (_jsxs(Link, { to: item.to, className: `app-header__dropdown-item ${item.active || item.to === activeItem ? 'active' : ''}`, onClick: (e) => onNavClick?.(item.to, e), children: [item.icon && _jsx("span", { className: "app-header__nav-icon", children: item.icon }), item.label] }, item.to))) }))] }));
}
/**
 * AppHeader - Flexible navigation header component
 *
 * Supports multiple variants:
 * - `glass`: Glass morphism effect for internal apps
 * - `minimal`: Simple header for external apps
 * - `portal`: Services portal style with nav grid
 *
 * @example
 * ```tsx
 * import { AppHeader } from '@shared/ui-react';
 * import { Link } from 'react-router-dom';
 *
 * // Internal app with dropdowns
 * <AppHeader
 *   appName="AccountingFlare"
 *   logoUrl="/logo.png"
 *   variant="glass"
 *   sticky
 *   navItems={[{ to: '/dashboard', label: 'Dashboard' }]}
 *   dropdownGroups={[{
 *     label: 'Reports',
 *     items: [
 *       { to: '/reports/ebay', label: 'eBay' },
 *       { to: '/reports/paypal', label: 'PayPal' },
 *     ],
 *     columns: 2
 *   }]}
 *   showThemeToggle
 *   showUserWidget
 *   LinkComponent={Link}
 * />
 *
 * // External app with simple nav
 * <AppHeader
 *   appName="ShopBeaver Storefront"
 *   variant="minimal"
 *   navItems={[
 *     { to: '/', label: 'Home' },
 *     { to: '/shop', label: 'Shop' },
 *   ]}
 *   LinkComponent={Link}
 * />
 * ```
 */
export function AppHeader({ appName, logoUrl, homeUrl = '/', navItems = [], dropdownGroups = [], breadcrumbs = [], activeItem, actions, showThemeToggle = true, showUserWidget = true, variant = 'glass', sticky = false, enableMobileMenu = true, brandLinks = [], subtitle, LinkComponent, onNavClick, onBreadcrumbClick, onHashChange, customDropdownRenderer, className = '', children, }) {
    const [mobileOpen, setMobileOpen] = useState(false);
    const Link = LinkComponent || 'a';
    // Hash routing support
    useEffect(() => {
        if (!onHashChange)
            return;
        const handleHashChange = () => {
            const hash = window.location.hash.slice(1); // Remove #
            onHashChange(hash);
        };
        window.addEventListener('hashchange', handleHashChange);
        return () => window.removeEventListener('hashchange', handleHashChange);
    }, [onHashChange]);
    const handleNavClick = useCallback((to, e) => {
        if (onNavClick) {
            onNavClick(to, e);
        }
        setMobileOpen(false);
    }, [onNavClick]);
    const variantClass = `app-header--${variant}`;
    const stickyClass = sticky ? 'app-header--sticky' : '';
    return (_jsxs("header", { className: `app-header ${variantClass} ${stickyClass} ${className}`.trim(), children: [_jsxs("div", { className: "app-header__content", children: [_jsxs("div", { className: "app-header__branding", children: [_jsxs(Link, { to: homeUrl, className: "app-header__logo-link", onClick: (e) => handleNavClick(homeUrl, e), children: [logoUrl && _jsx("img", { src: logoUrl, alt: `${appName} logo`, className: "app-header__logo" }), _jsxs("div", { className: "app-header__title-group", children: [_jsx("span", { className: "app-header__app-name", children: appName }), subtitle && _jsx("span", { className: "app-header__subtitle", children: subtitle })] })] }), _jsx("div", { id: "wt-app-branding-mount" })] }), enableMobileMenu && (navItems.length > 0 || dropdownGroups.length > 0) && (_jsx("button", { className: "app-header__mobile-toggle", onClick: () => setMobileOpen(!mobileOpen), "aria-label": "Toggle navigation menu", "aria-expanded": mobileOpen, children: _jsx("span", { className: `app-header__hamburger ${mobileOpen ? 'app-header__hamburger--open' : ''}` }) })), _jsxs("nav", { className: `app-header__nav ${mobileOpen ? 'app-header__nav--open' : ''}`, children: [navItems.map((item) => (_jsxs(Link, { to: item.to, className: `app-header__nav-btn ${item.active || item.to === activeItem ? 'active' : ''}`, onClick: (e) => handleNavClick(item.to, e), children: [item.icon && _jsx("span", { className: "app-header__nav-icon", children: item.icon }), item.label] }, item.to))), dropdownGroups.map((group) => {
                                // Use custom renderer if provided
                                if (customDropdownRenderer) {
                                    return _jsx(React.Fragment, { children: customDropdownRenderer(group) }, group.label);
                                }
                                // Default dropdown rendering
                                return (_jsx(NavDropdown, { label: group.label, items: group.items, columns: group.columns, icon: group.icon, LinkComponent: LinkComponent, onNavClick: handleNavClick, activeItem: activeItem }, group.label));
                            }), brandLinks.length > 0 && (_jsx("div", { className: "app-header__brand-links", children: brandLinks.map((link) => (_jsxs(Link, { to: link.to, className: `app-header__brand-btn app-header__brand-btn--${link.brand}`, onClick: (e) => handleNavClick(link.to, e), children: [link.logo && _jsx("img", { src: link.logo, alt: `${link.label} logo` }), link.label] }, link.to))) })), _jsx("div", { className: "app-header__spacer" }), children] }), _jsxs("div", { className: "app-header__controls", children: [actions, showThemeToggle && _jsx("div", { id: "theme-toggle-mount" }), showUserWidget && _jsx("div", { id: "wt-user-widget-mount" })] })] }), breadcrumbs.length > 0 && (_jsx("nav", { className: "app-header__breadcrumbs", "aria-label": "Breadcrumb", children: breadcrumbs.map((crumb, index) => (_jsxs(React.Fragment, { children: [index > 0 && _jsx("span", { className: "app-header__breadcrumb-sep", children: "/" }), crumb.href ? (_jsx("a", { href: crumb.href, className: "app-header__breadcrumb-link", onClick: (e) => onBreadcrumbClick?.(crumb.href, e), children: crumb.label })) : (_jsx("span", { className: "app-header__breadcrumb-current", children: crumb.label }))] }, crumb.label))) }))] }));
}
